/**
 * Labels, defaults and input config for the discount break-even calculator.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

export const STRINGS = {
  toolTitle: "Your numbers",
  price: "Selling price",
  cost: "Unit cost (COGS)",
  discount: "Planned discount",
  discountedPrice: "Discounted price",
  marginBefore: "Margin per unit before",
  marginAfter: "Margin per unit after",
  multiplier: "Break-even sales multiplier",
  lossPerUnit: "Loss per unit",
  disclaimer:
    "Estimates only, based on the numbers you enter. This holds gross profit constant and doesn't account for fixed costs, returns or the effect of price on demand.",
} as const;

/** Input ranges and step sizes. Inputs are clamped to these bounds. */
export const RANGES = {
  // Money fields accept a wide range; the ceiling just guards against overflow.
  money: { min: 0, max: 1_000_000, step: 1 },
  // Discount slider stops at 99%: a 100% giveaway isn't a pricing decision, and
  // stopping short keeps the slider in the range founders actually use while
  // still reaching the loss-making zone for high-margin products.
  discount: { min: 0, max: 99, step: 1 },
} as const;

/**
 * Neutral, round placeholder values the user is meant to change. Deliberately
 * not derived from any Wayflyer merchant data. A 100 / 40 split is a clean 60%
 * margin that yields a non-trivial result on load so the tool doesn't look
 * inert.
 */
export const DEFAULTS = {
  price: 100,
  cost: 40,
  discountPct: 25,
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Discount break-even FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a discount break-even calculator?",
    answer:
      "It's a calculator that shows how many extra units you need to sell at a discounted price to end up with the same total gross profit as if you'd sold fewer units at full price. Enter your selling price, unit cost and planned discount, and it works out the exact sales lift the discount needs to pay for itself.",
  },
  {
    question:
      "How many more units do I need to sell to make up for a discount?",
    answer:
      "It depends entirely on your margin before the discount, not the discount percentage alone. A deep discount on a high-margin product can need a smaller sales lift than a shallow discount on a thin-margin one, because the discount eats a bigger share of a thin margin. This tool works it out from your own numbers rather than a rule of thumb.",
  },
  {
    question: "What does the break-even sales multiplier mean?",
    answer:
      "It's the required uplift expressed as a multiple of your current volume. A ×1.5 multiplier means you need to sell 50% more units than you would at full price just to hold the same gross profit; ×2 means double the units.",
  },
  {
    question: "What if my result shows I'd lose money on every unit?",
    answer:
      "That happens when the discounted price falls at or below your unit cost, meaning no amount of extra volume can make up the difference; you lose money on every additional sale. The tool flags this directly and shows the loss per unit, so you can catch it before running the promotion rather than after.",
  },
  {
    question:
      "Does this account for fixed costs or how a discount changes demand?",
    answer:
      "No. This holds gross profit constant based on the price, cost and discount you enter; it doesn't account for fixed costs, returns, or the fact that a discount usually does change how much you sell. Treat the required uplift as the bar a discount needs to clear, not a demand forecast.",
  },
  {
    question: "Where can I get help with other Black Friday pricing decisions?",
    answer:
      "Check a multi-item bundle with the [bundle pricing calculator](/tools/bundle-pricing), see whether financing extra stock pays for itself with the [financing break-even calculator](/tools/financing-break-even), or browse the [full Black Friday toolkit](/tools).",
  },
];
